import React from 'react';
import Plot from 'react-plotly.js';

const SpectrumChart = ({ data, title }) => {
  if (!data) return null;

  const { spectrumData, harmonicFreqs, harmonicDBs, baseFreq, basedB, mean, std, thresholdStrong } = data;

  const traces = [];

  // Add spectrum data trace if available
  if (spectrumData && spectrumData.length > 0) {
    traces.push({
      x: spectrumData.map(d => d.frequency || d.Freq),
      y: spectrumData.map(d => d.db_level || d.dB),
      type: 'scatter',
      mode: 'lines',
      name: 'Spectrum',
      line: { color: '#6B7280', width: 1.2 },
      opacity: 0.7
    });
  }

  // Add harmonics trace if available
  if (harmonicFreqs && harmonicDBs && harmonicFreqs.length > 0) {
    traces.push({
      x: harmonicFreqs,
      y: harmonicDBs,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Harmonics',
      line: { color: '#3B82F6', width: 1.5 },
      marker: { color: '#EF4444', size: 6 }
    });
  }

  const shapes = [
    // Base frequency vertical line
    {
      type: 'line',
      x0: baseFreq, x1: baseFreq,
      y0: -200, y1: 50,
      line: { color: '#10B981', width: 1, dash: 'dash' }
    },
    // Base dB horizontal line (from 0 to base frequency)
    {
      type: 'line',
      x0: 0, x1: baseFreq,
      y0: basedB, y1: basedB,
      line: { color: '#10B981', width: 1, dash: 'dash' }
    },
    // Mean line
    {
      type: 'line',
      x0: 0, x1: 5000,
      y0: mean, y1: mean,
      line: { color: '#22C55E', width: 1.5 }
    },
    // Mean + std line
    {
      type: 'line',
      x0: 0, x1: 5000,
      y0: mean + (std || 0), y1: mean + (std || 0),
      line: { color: '#10B981', width: 1, dash: 'dash' }
    },
    // Mean - std line
    {
      type: 'line',
      x0: 0, x1: 5000,
      y0: mean - (std || 0), y1: mean - (std || 0),
      line: { color: '#10B981', width: 1, dash: 'dash' }
    },
    // Threshold lines
    {
      type: 'line',
      x0: 0, x1: 5000,
      y0: thresholdStrong, y1: thresholdStrong,
      line: { color: '#EF4444', width: 1, dash: 'dot' }
    },
    {
      type: 'line',
      x0: 0, x1: 5000,
      y0: -60, y1: -60,
      line: { color: '#3B82F6', width: 1, dash: 'dot' }
    },
    // Green shaded area between mean ± std
    {
      type: 'rect',
      x0: 0, x1: 5000,
      y0: mean - (std || 0), y1: mean + (std || 0),
      fillcolor: '#10B981',
      opacity: 0.2,
      line: { width: 0 }
    }
  ];

  // Add text annotations for values
  const annotations = [
    // Base frequency annotation
    {
      x: 5,
      y: basedB + 2,
      text: `Base ${baseFreq} Hz / ${basedB?.toFixed(2)} dB`,
      showarrow: false,
      font: { size: 10, color: '#10B981' },
      xanchor: 'left'
    },
    // Mean value annotation
    {
      x: 5,
      y: mean + 1,
      text: `${mean?.toFixed(2)}`,
      showarrow: false,
      font: { size: 9, color: '#22C55E' },
      xanchor: 'left'
    },
    // Mean + std annotation
    {
      x: 5,
      y: mean + (std || 0) + 1,
      text: `${(mean + (std || 0))?.toFixed(2)}`,
      showarrow: false,
      font: { size: 9, color: '#10B981' },
      xanchor: 'left'
    },
    // Mean - std annotation
    {
      x: 5,
      y: mean - (std || 0) - 3,
      text: `${(mean - (std || 0))?.toFixed(2)}`,
      showarrow: false,
      font: { size: 9, color: '#10B981' },
      xanchor: 'left'
    }
  ];

  const layout = {
    title: {
      text: title,
      font: { color: '#F3F4F6' }
    },
    xaxis: { 
      title: 'Frequency (Hz)', 
      range: [0, 5000],
      gridcolor: '#374151',
      showgrid: true,
      color: '#9CA3AF'
    },
    yaxis: { 
      title: 'Level (dB)',
      gridcolor: '#374151',
      showgrid: true,
      color: '#9CA3AF'
    },
    plot_bgcolor: '#1F2937',
    paper_bgcolor: '#111827',
    shapes: shapes,
    annotations: annotations,
    showlegend: true,
    legend: {
      x: 1,
      y: 1,
      xanchor: 'right',
      yanchor: 'top',
      font: { color: '#F3F4F6' },
      bgcolor: 'rgba(31, 41, 55, 0.8)'
    },
    width: 1000,
    height: 500,
    margin: { l: 60, r: 60, t: 60, b: 60 }
  };

  // If no traces available, show a message
  if (traces.length === 0) {
    return (
      <div className="bg-gray-900 rounded-lg p-8 text-center border border-gray-800">
        <p className="text-gray-400 text-lg">📊 Chart data not available</p>
        <p className="text-gray-500 text-sm mt-2">Only harmonic analysis results are shown in the table below.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
      <Plot data={traces} layout={layout} config={{ responsive: true }} />
    </div>
  );
};

export default SpectrumChart;
